import type { AlchemyType } from '@/domain/genetics/types';

/** 4 biomas do MVP. */
export type BiomeId = 'meadow' | 'forest' | 'cave' | 'coast';

export const ALL_BIOMES: ReadonlyArray<BiomeId> = ['meadow', 'forest', 'cave', 'coast'];

export interface BiomeDef {
  id: BiomeId;
  /** Multiplicador de decay de vitalidade por tipo elemental.
   * > 1 = ambiente hostil para o tipo; < 1 = resistência. */
  exposure: Record<AlchemyType, number>;
}

/** Tipos de items coletáveis (4 no MVP). */
export type ItemType = 'berry' | 'shell' | 'crystal' | 'flower';

export const ALL_ITEM_TYPES: ReadonlyArray<ItemType> = ['berry', 'shell', 'crystal', 'flower'];

export type Inventory = Record<ItemType, number>;

export function emptyInventory(): Inventory {
  return { berry: 0, shell: 0, crystal: 0, flower: 0 };
}

export interface RegionItem {
  /** id determinístico: `${biomeId}-${dayCount}-${idx}`. */
  id: string;
  type: ItemType;
  /** Coordenadas em pixels lógicos no grid mundial 80x60 (×TILE_SIZE). */
  x: number;
  y: number;
  collected: boolean;
}

export interface Region {
  id: BiomeId;
  /** Bounds em pixels lógicos. */
  bounds: { x: number; y: number; w: number; h: number };
  /** Obstáculos pintados pelo bioma — rects bloqueantes (em coords absolutas). */
  obstacles: Array<{ x: number; y: number; w: number; h: number }>;
  /** Posições fixas onde NPCs podem aparecer (uma por NPC). */
  npcSpots: Array<{ x: number; y: number }>;
}

export interface WorldState {
  position?: { x: number; y: number };
  regionsDiscovered: BiomeId[];
  inventory: Inventory;
  /** floor((now - createdAt) / DAY_MS_EFFECTIVE). Items respawnam quando avança. */
  dayCount: number;
  /** Items remaining por bioma (regenera quando dayCount muda). */
  regionItems: Record<BiomeId, RegionItem[]>;
  /** Último bioma visitado (para reentry). */
  lastBiome: BiomeId | null;
  /** U6: events, trees, memorials, kills, afk dispatches, generation count */
  events?: import('./evolve').WorldEvent[];
  trees?: import('./evolve').Tree[];
  memorials?: import('./memorial').Memorial[];
  enemyKills?: import('./bestiary').EnemyKills;
  afkDispatches?: import('./afk').AfkDispatch[];
  generation?: number;
  wildBestiary?: import('./wild-bestiary').WildBestiary;
}

export type DayPhase = 'day' | 'night';

// ============================================================================
// Constants
// ============================================================================

export const WORLD_TICK_MS = 1_000;

export const VITALITY_MAX = 100;
export const VITALITY_DECAY_BASE = 0.5; // pt/seg
export const VITALITY_RESTORE_PER_S = 5; // pt/seg em casa
export const VITALITY_LOW_THRESHOLD = 20;
export const VITALITY_TELEPORT_PENALTY_HAPPINESS = 20;

export const DAY_MS_PROD = 24 * 3_600_000;
export const DAY_MS_FAST = DAY_MS_PROD / 100;

export const ITEMS_PER_REGION_MIN = 3;
export const ITEMS_PER_REGION_MAX = 6;
export const INVENTORY_MAX_PER_TYPE = 99;

/** Grid lógico do mundo. */
export const WORLD_GRID_W = 160;
export const WORLD_GRID_H = 120;
export const TILE_SIZE = 32;
export const WORLD_PIXEL_W = WORLD_GRID_W * TILE_SIZE; // 5120
export const WORLD_PIXEL_H = WORLD_GRID_H * TILE_SIZE; // 3840

export const PLAYER_SPEED = 120; // px/seg (pode ser modificado por weight)

export function emptyWorldState(): WorldState {
  return {
    regionsDiscovered: ['meadow'],
    inventory: emptyInventory(),
    dayCount: 0,
    regionItems: { meadow: [], forest: [], cave: [], coast: [] },
    lastBiome: 'meadow',
    events: [],
    trees: [],
    memorials: [],
    enemyKills: {},
    afkDispatches: [],
      generation: 0,
      wildBestiary: {},
  };
}
